/**
 * ActionButton — atom for every recordable action.
 *
 * Variants map to the semantic colors in statsTheme.actionColors. Display:
 *   - made    → green   (success)
 *   - missed  → red     (failure)
 *   - neutral → blue    (assist, rebound, etc.)
 *   - warning → orange  (foul, technical)
 *   - info    → cyan    (block, deflection)
 *   - opponent → dark red
 *   - tag     → club accent
 *
 * Features preserved from EstadisticasNew.js:
 *   - Big touch target (>= 48px)
 *   - Optional keyboard shortcut badge
 *   - Pressed state (scale + dim)
 *   - Optional success pulse animation when an action lands
 */

import React, { useState, useCallback } from 'react';
import { ButtonBase, Box, Typography } from '@mui/material';
import { useStatsTheme, withAlpha } from '../theme/statsTheme';

const variantToken = {
  made: 'made',
  missed: 'missed',
  neutral: 'neutral',
  warning: 'warning',
  info: 'info',
  opponent: 'opponent',
  tag: 'tag',
};

function resolveColor(actionColors, variant, accent) {
  if (variant === 'accent') return accent;
  const key = variantToken[variant] || 'neutral';
  return actionColors[key];
}

export default function ActionButton({
  variant = 'neutral',
  label,
  icon,
  shortcut,
  onClick,
  disabled = false,
  fullWidth = false,
  size = 'md',
  ariaLabel,
  sx,
}) {
  const theme = useStatsTheme();
  const [pulsing, setPulsing] = useState(false);

  const color = resolveColor(theme.actionColors, variant, theme.accent);

  const heights = { sm: 36, md: theme.touch.buttonHeight, lg: 64 };
  const fontSizes = { sm: 12, md: 14, lg: 16 };

  const handleClick = useCallback((e) => {
    if (disabled) return;
    setPulsing(true);
    setTimeout(() => setPulsing(false), 220);
    if (typeof onClick === 'function') onClick(e);
  }, [disabled, onClick]);

  return (
    <ButtonBase
      onClick={handleClick}
      disabled={disabled}
      aria-label={ariaLabel || label}
      focusRipple
      sx={{
        position: 'relative',
        width: fullWidth ? '100%' : 'auto',
        minWidth: heights[size],
        height: heights[size],
        px: 1.5,
        borderRadius: `${theme.radius.md}px`,
        backgroundColor: withAlpha(color, 0.18),
        border: `1px solid ${withAlpha(color, 0.42)}`,
        color: theme.text.primary,
        fontFamily: theme.typography.fontFamily,
        fontSize: fontSizes[size],
        fontWeight: 600,
        letterSpacing: '0.01em',
        boxShadow: theme.shadows.sm,
        transition: `transform ${theme.motion.fast}, background-color ${theme.motion.fast}, box-shadow ${theme.motion.fast}`,
        outline: 'none',
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'not-allowed' : 'pointer',
        '&:hover': {
          backgroundColor: withAlpha(color, 0.32),
          boxShadow: theme.shadows.md,
          transform: 'translateY(-1px)',
        },
        '&:active': {
          transform: 'scale(0.96)',
          backgroundColor: withAlpha(color, 0.45),
        },
        ...(pulsing && {
          animation: 'actionPulse 220ms ease-out',
        }),
        '@keyframes actionPulse': {
          '0%':   { boxShadow: `0 0 0 0 ${withAlpha(color, 0.7)}` },
          '100%': { boxShadow: `0 0 0 14px ${withAlpha(color, 0)}` },
        },
        ...sx,
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        {icon && <Box sx={{ display: 'flex', color }}>{icon}</Box>}
        {label && (
          <Typography
            component="span"
            sx={{
              fontSize: 'inherit',
              fontWeight: 'inherit',
              color: theme.text.primary,
              textTransform: 'uppercase',
              letterSpacing: '0.04em',
            }}
          >
            {label}
          </Typography>
        )}
      </Box>

      {shortcut && (
        <Box
          sx={{
            position: 'absolute',
            top: 4,
            right: 6,
            px: 0.6,
            minWidth: 16,
            height: 16,
            borderRadius: `${theme.radius.sm}px`,
            backgroundColor: withAlpha('#000000', 0.35),
            color: theme.text.secondary,
            fontSize: 9,
            fontWeight: 700,
            letterSpacing: '0.06em',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            textTransform: 'uppercase',
            pointerEvents: 'none',
          }}
        >
          {shortcut}
        </Box>
      )}
    </ButtonBase>
  );
}
